import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Card,
  FloatingLabel,
} from "react-bootstrap";
import { CheckCircleFill, ArrowRepeat } from "react-bootstrap-icons";
import apiService from "../services/apiService";
import { useNotification } from "./notification";
import { useAuth } from "../contexts/authContext";

const PhoneVerification = ({
  value = "",
  onChange,
  onVerified,
  disabled = false,

  /* ── account phone (e  .g. user.profile.phone) ────────────────────── */
  existingPhoneNumber = "",
  isAlreadyVerified = false,

  /* ── address phone (e.g. user.address.phone) ────────────────────── */
  isAddress = false,
  existingAddressPhone = "",
  isAddressPhoneVerified = false,
  id = "",
}) => {
  const { user } = useAuth();
  const existingPhone = user?.phone || existingPhoneNumber;
  const isExistingVerified = user?.phoneVerified || isAlreadyVerified;
  /* ─────────────────────────── state ───────────────────────────── */
  const [phone, setPhone]                     = useState(existingPhone || value );
  const [otp, setOtp]                         = useState("");
  const [countdown, setCountdown]             = useState(0);
  const [isVerified, setVerified]             = useState(isExistingVerified);
  const [isEditingVerified, setEditing]       = useState(false);
  const [showOtpField, setShowOtpField]       = useState(false);
  const [isPhoneAvailable, setPhoneAvailable] = useState(true);
  const [isLoading, setLoading]               = useState({
    check:  false,
    send:   false,
    verify: false,
  });

  const { showNotification } = useNotification();

  /* ─────────────── self‑documenting “truth‑table” ──────────────── */
  const computeStatus = useCallback(
    (num) => {
      const matchesExisting = num === existingPhone        && isExistingVerified;
      const matchesAddress  = isAddress &&
                              num === existingAddressPhone && isAddressPhoneVerified;

      const editingExisting = num !== existingPhone        && isExistingVerified;
      const editingAddress  = isAddress &&
                              num !== existingAddressPhone && isAddressPhoneVerified;

      return {
        verified:        matchesExisting || matchesAddress,
        editingVerified: editingExisting || editingAddress,
      };
    },
    [
      existingPhone,
      existingAddressPhone,
      isExistingVerified,
      isAddressPhoneVerified,
      isAddress,
    ]
  );

  /* ─────────────────── sync external value prop ────────────────── */
  useEffect(() => {
    if (value !== undefined && value !== phone) {
      handlePhoneChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /* ─────────────────────── countdown timer ─────────────────────── */
  useEffect(() => {
    if (!countdown) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  /* ─────────────── check if number is already taken ────────────── */
  const checkPhoneAvailability = async (num) => {
    if (!/^\d{10}$/.test(num)) return;
    if (num === existingPhone || num === existingAddressPhone) return;

    try {
      setLoading((s) => ({ ...s, check: true }));
      const res = await apiService.post("/otp/check-phone-number", { phone: num });

      if (!res?.success) {
        showNotification("danger", "This phone number is already registered");
      }
      setPhoneAvailable(res?.success === true);
    } catch (err) {
      showNotification("danger", err || "Failed to check phone number");
      setPhoneAvailable(false);
    } finally {
      setLoading((s) => ({ ...s, check: false }));
    }
  };

  /* ───────────────────────── send OTP ──────────────────────────── */
  const sendOtp = async () => {
    if (!/^\d{10}$/.test(phone)) {
      showNotification("danger", "Please enter a valid 10‑digit phone number");
      return;
    }

    try {
      setLoading((s) => ({ ...s, send: true }));
      await apiService.post("/otp/send-phoneOTP", { phone });
      showNotification("success", "OTP sent successfully");
      setShowOtpField(true);
      setOtp("");
      setCountdown(30);
    } catch (err) {
      showNotification("danger", err?.message || "Failed to send OTP");
    } finally {
      setLoading((s) => ({ ...s, send: false }));
    }
  };

  /* ─────────────────────── verify OTP ──────────────────────────── */
  const verifyOtp = async () => {
    if (otp.length !== 4) {
      showNotification("danger", "Please enter the 4‑digit OTP");
      return;
    }

    try {
      setLoading((s) => ({ ...s, verify: true }));
      const res = await apiService.post("/otp/verify-otp", { phone, code: otp });

      if (res?.success) {
        setVerified(true);
        setEditing(false);
        showNotification("success", "Phone number verified");
        setShowOtpField(false);
        onVerified?.(true);
      } else {
        showNotification("danger", "Invalid OTP code");
      }
    } catch (err) {
      showNotification("danger", err || "Verification failed");
    } finally {
      setLoading((s) => ({ ...s, verify: false }));
    }
  };

  /* ───────────────── update status when phone / props change ───── */
  useEffect(() => {
    if (!phone) return;

    const { verified, editingVerified } = computeStatus(phone);
    setVerified(verified);
    setEditing(editingVerified);
    onVerified?.(verified);

    /*  Only ping server if number is *not* already verified  */
    if (phone.length === 10 && !verified && !(existingPhone || existingAddressPhone)) {
      checkPhoneAvailability(phone);
    } else {
      setPhoneAvailable(true);
    }
  }, [phone, computeStatus, onVerified]);

  /* ───────────────── handle user typing ────────────────────────── */
  const handlePhoneChange = (raw) => {
    const num = raw ? raw.replace(/\D/g, "").slice(0, 10) : "";
    setPhone(num);
    onChange?.(num);
    setShowOtpField(false); // hide OTP field while editing
  };

  /* ─────────────────────────── UI ──────────────────────────────── */
  return (
    <div className="phone-verification">
      {/* ── phone input + “send OTP / verified” button ───────────── */}
      <Row className="mb-3 align-items-center">
        <Col md={8}>
          <FloatingLabel controlId={`phoneInput-${id}`} label="Phone Number">
            <Form.Control
              type="tel"
              value={phone}
              placeholder="10-digit mobile number"
              onChange={(e) => handlePhoneChange(e.target.value)}
              disabled={disabled}
              className={isVerified ? "border-success" : ""}
              isInvalid={phone.length > 0 && phone.length !== 10}
            />
            <Form.Control.Feedback type="invalid">
              Please enter a valid 10‑digit phone number
            </Form.Control.Feedback>
          </FloatingLabel>

          {isLoading.check && (
            <Form.Text className="text-muted">
              <Spinner size="sm" className="me-2" />
              Checking availability…
            </Form.Text>
          )}

          {/* {!isVerified &&
            phone.length === 10 &&
            !isLoading.check &&
            !isPhoneAvailable &&
            !isAddress && (
              <Form.Text className="text-danger">
                This phone number is already registered
              </Form.Text>
            )} */}
        </Col>

        <Col md={4} className="justify-content-end">
          {isVerified && phone.length === 10 ? (
            <Button variant="success" className="w-100" disabled>
              <CheckCircleFill className="me-2" />
              Verified
            </Button>
          ) : (
            <Button
              variant={isEditingVerified && (existingPhone || existingAddressPhone) ? "warning" : "primary"}
              onClick={sendOtp}
              disabled={
                disabled ||
                phone.length !== 10 ||
                !isPhoneAvailable ||
                isLoading.check ||
                isLoading.send
              }
              className="w-100"
            >
              {isLoading.send ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Sending…
                </>
              ) : isEditingVerified && isPhoneAvailable && (existingPhone || existingAddressPhone) ? (
                "Verify New Number"
              ) : (
                "Send OTP"
              )}
            </Button>
          )}
        </Col>
      </Row>

      {/* ── OTP input + verify button ─────────────────────────────── */}
      {showOtpField && !isVerified && (
        <Card className="shadow-sm mb-3">
          <Card.Body>
            <span className="mb-3 text-muted">
              Enter verification code sent to {phone}
            </span>

            <Row className="align-items-center g-2 mt-3">
              <Col md={8}>
                <Form.Control
                  type="text"
                  maxLength={4}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="text-center fw-bold fs-4 py-2"
                  placeholder="----"
                />
              </Col>
              <Col md={4}>
                <Button
                  variant="success"
                  onClick={verifyOtp}
                  disabled={isLoading.verify || otp.length !== 4}
                  className="w-100"
                >
                  {isLoading.verify ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Verifying…
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              </Col>
            </Row>

            <div className="mt-3 text-center">
              <Button
                variant="link"
                onClick={sendOtp}
                disabled={countdown > 0 || isLoading.send}
                className="text-decoration-none link-secondary fw-semibold"
              >
                <ArrowRepeat className="me-1" />
                {countdown > 0 ? `Resend in ${countdown}s` : "Resend code"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── yellow footnote while user edits a verified number ────── */}
      {isEditingVerified && isPhoneAvailable && (existingPhone || existingAddressPhone) && (
        <div className="text-warning small mt-2">
          You’re changing your verified phone number. Please verify the new
          number.
        </div>
      )}
    </div>
  );
};

export default PhoneVerification;